import { MutationFunction, useMutation, UseMutationResult, useQuery } from "@tanstack/react-query";
import { client } from "@johnpaul/jp-api-client";
import {
  getClientToken,
  getHashParams,
  hashPassword,
} from "@companion-core/shared/app/Utils/apiClient";
import { jwtDecode, JwtPayload } from "jwt-decode";

type UseMemberProps = {
  withSubscriptions?: boolean;
  enabled?: boolean;
  staleTime?: number;
};

export const useMember = ({
  withSubscriptions = true,
  enabled = true,
  staleTime = 0,
}: UseMemberProps = {}) => {
  return useQuery({
    queryKey: ["member"],
    queryFn: () => client.members.v3.get("me", withSubscriptions),
    enabled,
    staleTime,
    retry: false,
  });
};

export const useVerifyAccount = (accessTokenClient: string, vToken: string, enabled = true) => {
  return useQuery({
    queryKey: ["verifyAccount"],
    queryFn: () => client.members.v2.register.verifyAccount(accessTokenClient, vToken),
    enabled: enabled && !!accessTokenClient.length,
    retry: false,
    gcTime: 0,
  });
};

type RecoverPasswordVariables = {
  email: string;
};

export function useRecoverPassword(): UseMutationResult<unknown, Error, RecoverPasswordVariables> {
  const mutationFn: MutationFunction<unknown, RecoverPasswordVariables> = ({ email }) => {
    return getClientToken().then((response: string) => {
      const token = { "access_token_client": response };
      return client.password.recover({ email, token });
    });
  };

  return useMutation<unknown, Error, RecoverPasswordVariables>({
    mutationFn,
    retry: false,
    gcTime: 0,
  });
}

type ResetPasswordVariables = {
  password: string;
  token: string;
  question?: string;
  answer?: string;
};

type ResetError = {
  response?: {
    data?: {
      error_description?: {
        password?: [string];
      };
    };
  };
};

export function useResetPassword(
  onError?: (error: ResetError) => void
): UseMutationResult<unknown, ResetError, ResetPasswordVariables> {
  const mutationFn: MutationFunction<unknown, ResetPasswordVariables> = async ({
    password,
    token,
    question,
    answer,
  }) => {
    return getClientToken().then(async (response: string) => {
      const clientToken = { "access_token_client": response };
      const jwt = jwtDecode<JwtPayload>(token);
      // eslint-disable-next-line camelcase
      const { hash_len, m, p, salt, t } = await getHashParams({
        sub: jwt.sub,
        token: response,
      });
      const hashedPassword = await hashPassword(password, salt, t, m, p, hash_len);
      const hashedAnswer = answer ? await hashPassword(answer, salt, t, m, p, hash_len) : null;
      return await client.password.reset({
        password: hashedPassword,
        token,
        clientToken,
        sub: jwt.sub,
        question,
        answer: hashedAnswer,
        isHashed: true,
      });
    });
  };

  return useMutation({
    mutationFn,
    retry: false,
    gcTime: 0,
    onError: onError ? onError : undefined,
  });
}

type UpdateMemberVariables = {
  id: string;
  payload: Record<string, unknown>;
};

export function useUpdateMember(onError?: () => void) {
  const mutationFn: MutationFunction<unknown, UpdateMemberVariables> = async ({ id, payload }) => {
    return await client.members.v3.update(id, payload);
  };

  return useMutation({
    mutationFn,
    retry: false,
    gcTime: 0,
    onError: onError ? onError : undefined,
  });
}
